import React, {useMemo, useState} from "react"
import type {TypographyProps} from '@mui/material/Typography';
import {usePageContext} from "../../../context/PageContext";
import {useParentComponentContext} from "../../../context/ParentComponentContext";
import {renderCharacters} from "../CCOPlainText";
import {
    enrichWithUuid,
    getTextChild,
    ListType,
    renderTruncatedElement,
    RichTextElement,
    RichTextProps,
} from "./CCORichTextHelper";
import {
    renderBlockquoteElement,
    renderDefaultElement,
    renderDivElement,
    renderEmElement,
    renderLiElement,
    renderLinkElement,
    renderMediaElement,
    renderOlElement,
    renderShowMoreContainerElement,
    renderStrongElement,
    renderTableElement,
    renderTBodyElement,
    renderTdElement,
    renderTextElement,
    renderThElement,
    renderTrElement,
    renderUlElement
} from "./CCORichTextElementTypes";
import {
    addShowMoreWrapperIfNecessary,
    ElementsToWrap,
    wrapElementsAfterConsecutiveBrTagsInShowMoreAndReturn
} from "./CCORichTextShowMoreWrapper";
import PreviewPropertyHighlighter from "../../preview/PreviewPropertyHighlighter";

type Variant = TypographyProps['variant'];
type RenderElement = (props: RichTextProps,
                      element: RichTextElement,
                      parentComponentId: string,
                      pageId: string,
                      key?: string,
                      variant?: Variant | 'blockQuote',
                      showMore?: boolean,
                      setShowMore?: any,
                      listType?: ListType) => JSX.Element | null;

function CCORichText(props: RichTextProps) {
    const parentContext = useParentComponentContext();
    const pageContext = usePageContext();
    const [showMore, setShowMore] = useState(false);

    // Computed synchronously instead of in a useEffect, so this renders identically during SSR and
    // the first client render. An effect never runs on the server, so the previous
    // enrich-after-mount approach made this component return null on every server render -
    // hiding all rich text (paragraphs, FAQ answers, teaser/detail text, etc.) from crawlers that
    // don't execute JavaScript.
    const enrichedTextAsTree = useMemo(() => enrichWithUuid(props.textAsTree), [props.textAsTree]);

    if (!enrichedTextAsTree) {
        return null;
    }
    const enrichedProps = {...props, textAsTree: enrichedTextAsTree};

    // Created fresh for every render so that elements to wrap are not displayed twice (in the
    // element itself and in show more container) and so that two CCORichText instances rendering
    // at the same time do not collect into the same object.
    const childrenToWrap: ElementsToWrap = {elementsToDisplay: [], elementsToWrap: []};
    const renderElement = createRenderElement(childrenToWrap);

    const element = enrichedProps.charLimit
        ? renderTruncatedElement(enrichedProps, renderElement, getTextChild, showMore, setShowMore,
                                 parentContext?.cmId, pageContext?.page?.id)
        : renderElement(enrichedProps, enrichedProps.textAsTree, parentContext?.cmId, pageContext?.page?.id,
                        undefined, enrichedProps.variant, showMore, setShowMore);

    return <PreviewPropertyHighlighter propertyName={enrichedProps.propertyName}
                                       metadataId={enrichedProps.metadataId}>{addShowMoreWrapperIfNecessary(
        enrichedProps, element, childrenToWrap, renderElement, showMore, setShowMore, parentContext?.cmId,
        pageContext?.page?.id)}</PreviewPropertyHighlighter>
}

/**
 * Binds the per-render wrap target to the renderer, so that every recursive call and every helper
 * that receives the renderer as a callback collects into the same object.
 */
function createRenderElement(childrenToWrap: ElementsToWrap): RenderElement {
    function renderElement(props: RichTextProps,
                           element: RichTextElement,
                           parentComponentId: string,
                           pageId: string,
                           key?: string,
                           variant?: Variant | 'blockQuote',
                           showMore?: boolean,
                           setShowMore?: any,
                           listType?: ListType): JSX.Element | null {
        return renderElementInto(childrenToWrap, renderElement, props, element, parentComponentId,
                                 pageId, key, variant, showMore, setShowMore, listType);
    }

    return renderElement;
}

function renderElementInto(childrenToWrap: ElementsToWrap,
                           renderElement: RenderElement,
                           props: RichTextProps,
                           element: RichTextElement,
                           parentComponentId: string,
                           pageId: string,
                           key?: string,
                           variant?: Variant | 'blockQuote',
                           showMore?: boolean,
                           setShowMore?: any,
                           listType?: ListType): JSX.Element | null {

    const wordsLimit = props.wordsLimit;
    const charLimit = props.charLimit;
    const strong = props.strong;
    const color = props.color;

    if (wrapElementsAfterConsecutiveBrTagsInShowMoreAndReturn(props, element, childrenToWrap)) {
        return null
    }

    const renderChildrenRecursively = (e: RichTextElement, index: number) => {
        const child = renderElement(props, e, parentComponentId, pageId, index.toString(), variant, showMore, setShowMore);
        if (!child) {
            return null;
        }
        const key = e.uuid ?? index;
        return React.isValidElement(child) ? React.cloneElement(child, {key}) : child;
    };

    if (!element || !element._type) {
        return null;
    }
    switch (element._type) {
        case "Element": {
            switch (element.name) {
                case "div":
                case "p":
                case "h1":
                case "h2":
                case "h3":
                case "h4":
                case "h5":
                case "h6": {
                    return renderTextElement(element, props, renderChildrenRecursively, key, color,
                                             strong)
                }
                case "strong": {
                    return renderStrongElement(element, renderChildrenRecursively)
                }
                case "em": {
                    return renderEmElement(element, renderChildrenRecursively, color)
                }
                case "blockquote": {
                    return renderBlockquoteElement(element, props, renderElement, showMore,
                                                   setShowMore, parentComponentId, pageId)
                }
                case "ul": {
                    return renderUlElement(element, props, renderElement, showMore, setShowMore,
                                           parentComponentId, pageId)
                }
                case "ol": {
                    return renderOlElement(element, props, renderElement, showMore, setShowMore,
                                           parentComponentId, pageId)
                }
                case "li": {
                    return renderLiElement(listType, element, renderChildrenRecursively)
                }
                case "a": {
                    return renderLinkElement(element, props, renderChildrenRecursively, key,
                                             parentComponentId, pageId)
                }
                case "showMoreWrapper": {
                    return renderShowMoreContainerElement(element, renderChildrenRecursively,
                                                          showMore, setShowMore, key)
                }
                case "table": {
                    return renderTableElement(element, renderChildrenRecursively);
                }
                case "tr": {
                    return renderTrElement(element, renderChildrenRecursively);
                }
                case "td": {
                    return renderTdElement(element, renderChildrenRecursively);
                }
                case "th": {
                    return renderThElement(element, renderChildrenRecursively);
                }
                case "tbody": {
                    return renderTBodyElement(element, renderChildrenRecursively);
                }
                default: {
                    return renderDefaultElement(element, renderChildrenRecursively)
                }
            }
        }
        case "EmptyElement": {
            switch (element.name) {
                case "br": {
                    return <br key={element.uuid}/>
                }
                case "img": {
                    return renderMediaElement(element, key, props)
                }
                default: {
                    throw Error(`Unsupported RichTextElement name '${element.name}'`)
                }
            }
        }
        case "Characters": {
            return renderCharacters(element, charLimit, wordsLimit)
        }
        default: {
            switch (element.name) {
                case "div": {
                    return renderDivElement(element, renderChildrenRecursively)
                }
                default: {
                    throw Error(`Unsupported RichTextElement type '${element._type}'`)
                }
            }
        }
    }
}

export default CCORichText
